import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { BadgeDollarSign } from "lucide-react";
import axios from "axios";
import Select from "react-select";
import authHeader from "../../../Auth/authHeader";
import { getAuthData } from "../../../Auth/getAuthData";
import { ApiLink } from "../../../APILink/ApiLink";
import { fetchActiveCompanyUnits } from "../../../GetData/CompanyUnitsData";
import "./SalesConfigTab.scss";

export default function SalesConfigTab({ initialData, onUpdate }) {
  const authData = getAuthData();
  const userCompanyUnitID = Number(authData?.companyUnitId || 0);
  const isCompanyUnitUser = userCompanyUnitID > 0;
  const [data, setData] = useState(initialData || {});
  const [companyUnits, setCompanyUnits] = useState([]);
  const [savingKey, setSavingKey] = useState(null);

  useEffect(() => {
    setData(initialData || {});
  }, [initialData]);

  useEffect(() => {
    let isMounted = true;

    if (isCompanyUnitUser) {
      setCompanyUnits([]);
      return undefined;
    }

    const loadData = async () => {
      const units = await fetchActiveCompanyUnits();
      if (!isMounted) return;
      setCompanyUnits(Array.isArray(units) ? units : []);
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [isCompanyUnitUser]);

  const currencyOptions = [
    { value: "ALL", label: "ALL - Albanian Lek" },
    { value: "EUR", label: "EUR - Euro" },
    { value: "USD", label: "USD - US Dollar" },
    { value: "GBP", label: "GBP - British Pound" },
    { value: "CHF", label: "CHF - Swiss Franc" },
    { value: "MKD", label: "MKD - Macedonian Denar" },
    { value: "RSD", label: "RSD - Serbian Dinar" },
    { value: "BAM", label: "BAM - Bosnia and Herzegovina Mark" },
    { value: "TRY", label: "TRY - Turkish Lira" },
    { value: "CAD", label: "CAD - Canadian Dollar" },
    { value: "AUD", label: "AUD - Australian Dollar" },
    { value: "NZD", label: "NZD - New Zealand Dollar" },
    { value: "JPY", label: "JPY - Japanese Yen" },
    { value: "CNY", label: "CNY - Chinese Yuan" },
    { value: "SEK", label: "SEK - Swedish Krona" },
    { value: "NOK", label: "NOK - Norwegian Krone" },
    { value: "DKK", label: "DKK - Danish Krone" },
    { value: "PLN", label: "PLN - Polish Zloty" },
    { value: "CZK", label: "CZK - Czech Koruna" },
    { value: "HUF", label: "HUF - Hungarian Forint" },
    { value: "RON", label: "RON - Romanian Leu" },
    { value: "BGN", label: "BGN - Bulgarian Lev" },
    { value: "HRK", label: "HRK - Croatian Kuna" },
    { value: "AED", label: "AED - UAE Dirham" },
    { value: "SAR", label: "SAR - Saudi Riyal" },
  ];

  const currentCurrency = (data.currencyOptions || "").toUpperCase();
  const hasCurrentCurrency = currencyOptions.some((opt) => opt.value === currentCurrency);
  const selectableCurrencies = currentCurrency && !hasCurrentCurrency
    ? [{ value: currentCurrency, label: `${currentCurrency} - Valuta aktuale` }, ...currencyOptions]
    : currencyOptions;

  const warehouseOptions = useMemo(
    () => (companyUnits || []).map((unit) => ({ value: String(unit.UnitID), label: unit.UnitName })),
    [companyUnits],
  );

  const selectedWarehouseOption = warehouseOptions.find((opt) => opt.value === String(data.mainDepo || "")) || null;
  const selectedCurrencyOption = selectableCurrencies.find((opt) => opt.value === currentCurrency) || null;
  const selectedSalesType = String(data.salesTypeId || "");

  const searchableSelectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: 42,
      borderColor: state.isFocused ? "#284945" : "#cbd5e1",
      boxShadow: state.isFocused ? "0 0 0 3px rgba(40, 73, 69, 0.12)" : "none",
      "&:hover": { borderColor: "#284945" },
      borderRadius: 8,
      fontSize: 14,
    }),
    option: (base, state) => ({
      ...base,
      fontSize: 14,
      backgroundColor: state.isFocused ? "#f1f5f9" : "#fff",
      color: "#0f172a",
      cursor: "pointer",
    }),
    menu: (base) => ({ ...base, zIndex: 9999 }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  };

  const saveSalesConfig = async (payload) => {
    const headers = authHeader();
    await axios.put(ApiLink.API_URL + "Companies/updateSalesConfig", payload, { headers });
  };

  const saveUnitSalesConfig = async (payload) => {
    const headers = authHeader();
    await axios.put(ApiLink.API_URL + "CompanyUnits/updateSalesConfig", payload, { headers });
  };

  const handleAutoSave = async (key, value, title) => {
    if (savingKey || (!data.companyId && !isCompanyUnitUser)) return;

    const previousData = { ...data };
    const updatedRecord = {
      ...data,
      [key]: value,
      id: data.id || data.companyId,
    };

    setData(updatedRecord);
    setSavingKey(key);

    try {
      if (isCompanyUnitUser) {
        const payload = {
          UnitID: userCompanyUnitID,
          HasSalesTypeID: key === "salesTypeId",
          HasMainDepo: false,
          HasCurrencyOptions: key === "currencyOptions",
        };

        if (key === "salesTypeId") {
          payload.SalesTypeID = value ? Number(value) : null;
        }

        if (key === "currencyOptions") {
          payload.CurrencyOptions = value || null;
        }

        await saveUnitSalesConfig(payload);
      } else {
        const payload = {
          CompanyID: updatedRecord.companyId,
          HasSalesTypeID: key === "salesTypeId",
          HasMainDepo: key === "mainDepo",
          HasCurrencyOptions: key === "currencyOptions",
        };

        if (key === "salesTypeId") {
          payload.SalesTypeID = value ? Number(value) : null;
        }

        if (key === "mainDepo") {
          payload.MainDepo = value ? Number(value) : null;
        }

        if (key === "currencyOptions") {
          payload.CurrencyOptions = value || null;
        }

        await saveSalesConfig(payload);
      }

      onUpdate({ ...updatedRecord });

      toast.success(`${title} - u perditesua me sukses.`, {
        duration: 3000,
        style: {
          background: "#f0fdf4",
          color: "#15803d",
          border: "1px solid #bbf7d0",
          fontWeight: "600",
          fontSize: "0.875rem",
        },
        iconTheme: { primary: "#16a34a", secondary: "#f0fdf4" },
      });
    } catch (error) {
      setData(previousData);
      toast.error(`Ndryshimi i ${title.toLowerCase()} deshtoi.`, {
        duration: 4000,
        style: {
          background: "#fef2f2",
          color: "#991b1b",
          border: "1px solid #fecaca",
          fontWeight: "600",
          fontSize: "0.875rem",
        },
        iconTheme: { primary: "#dc2626", secondary: "#fef2f2" },
      });
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <div className="sales-config-tab">
      <div className="sales-config-tab__header">
        <h2 className="sales-config-tab__title">
          <BadgeDollarSign className="sales-config-tab__title-icon" />
          Konfigurimi i Shitjeve dhe POS
        </h2>
        <p className="sales-config-tab__subtitle">
          Konfiguro vlerat dhe rregullat baze per piken e shitjes dhe faturimin.
        </p>
      </div>

      <div className="sales-config-tab__card">
        <div className="sales-config-tab__grid">
          
          <div className="sales-config-tab__field">
            <label htmlFor="defaultSalesPrice" className="sales-config-tab__field-label">Niveli Baze i Cmimit te Shitjes</label>
            <div>
              <select
                id="defaultSalesPrice"
                name="salesTypeId"
                value={selectedSalesType}
                onChange={(e) => handleAutoSave("salesTypeId", e.target.value, "Lloji i shitjes")}
                className="sales-config-tab__select"
                disabled={savingKey !== null}
              >
                <option value="">Zgjidh llojin e shitjes</option>
                <option value="1">Shitje me Pakice</option>
                <option value="2">Shitje me Shumice</option>
              </select>
            </div>
          </div>

          {!isCompanyUnitUser && (
            <div className="sales-config-tab__field">
              <label htmlFor="defaultWarehouse" className="sales-config-tab__field-label">Depoja Baze</label>
              <Select
                inputId="defaultWarehouse"
                id="defaultWarehouse"
                name="mainDepo"
                value={selectedWarehouseOption}
                options={warehouseOptions}
                onChange={(option) => {
                  handleAutoSave("mainDepo", option ? option.value : "", "Depoja baze");
                }}
                placeholder="Shkruaj per te gjetur depon..."
                isSearchable
                isClearable
                styles={searchableSelectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                noOptionsMessage={() => "Nuk u gjet asnje depo"}
                isDisabled={savingKey !== null}
              />
            </div>
          )}

          <div className="sales-config-tab__field">
            <label htmlFor="defaultCurrency" className="sales-config-tab__field-label">Valuta Baze</label>
            <Select
              inputId="defaultCurrency"
              id="defaultCurrency"
              name="currencyOptions"
              value={selectedCurrencyOption}
              options={selectableCurrencies}
              onChange={(option) => {
                handleAutoSave("currencyOptions", option ? option.value : "", "Valuta baze");
              }}
              placeholder="Shkruaj kodin ose emrin e valutes..."
              isSearchable
              isClearable
              styles={searchableSelectStyles}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              noOptionsMessage={() => "Nuk u gjet asnje valute"}
              isDisabled={savingKey !== null}
            />
          </div>
        </div>
      </div>

      {/* Menyra Baze e Pageses dhe Lejo Ndryshimin Manual te Cmimit jane hequr perkohesisht nga UI. */}
    </div>
  );
}